import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { TextInput } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { CampaignSetupContext } from "../../../context/CampaignSetupContext";
import "./ActivityStreakConfigController.scss";

const ActivityStreakConfigController = (props) => {
  const { data = [], handleChange, path, visible = true, schema = {} } = props;
  const { campaignState } = useContext(CampaignSetupContext);
  const activities = campaignState.activities || [];
  // Access the full form data using useJsonForms hook
  const ctx = useJsonForms();
  const jsonFormState = ctx?.core?.data || {};
  const requiredActivities = jsonFormState.required_activities;

  // Determine required fields based on required_activities value and schema
  const getRequiredFields = () => {
    let schemaRequired = [];

    if (schema?.items?.required) {
      // Use schema required fields if they exist
      schemaRequired = Array.isArray(schema.items.required)
        ? schema.items.required
        : [];
    } else if (!schema || Object.keys(schema).length === 0) {
      // Only use default required fields if no schema is provided at all
      schemaRequired = [];
    } else {
      // Schema exists but has no items.required - use defaults
      schemaRequired = ["target_points", "label_singular", "label_plural"];
    }

    // Then add conditional fields based on required_activities
    if (requiredActivities === "ALL") {
      return [...schemaRequired, "activity_id", "activity_function"];
    }
    return schemaRequired;
  };

  const currentRequiredFields = getRequiredFields();
  // Initialize config from activities and any existing data
  const [config, setConfig] = useState([]);

  useEffect(() => {
    let initialConfig = [];

    if (requiredActivities === "ANY") {
      // For ANY mode: single generic configuration
      const existing = data[0] || {};
      initialConfig = [
        {
          target_points: existing.target_points || "",
          label_singular: existing.label_singular || "",
          label_plural: existing.label_plural || "",
        },
      ];
    } else {
      // For ALL mode (or undefined - default): activity-specific configurations
      initialConfig = activities.map((activity, idx) => {
        const existing = data[idx] || {};
        return {
          activity_id: activity.id,
          activity_function: activity.activityDefinition?.slug,
          name: activity.activityDefinition?.name || "",
          target_points: existing.target_points || "",
          label_singular: existing.label_singular || "",
          label_plural: existing.label_plural || "",
        };
      });
    }

    // Only update if different
    if (JSON.stringify(initialConfig) !== JSON.stringify(config)) {
      setConfig(initialConfig);
    }
    // eslint-disable-next-line
  }, [activities, data, requiredActivities]);

  const handleFieldChange = (idx, field, value) => {
    const updated = [...config];
    updated[idx][field] = value;
    //remove name
    delete updated[idx].name;
    setConfig(updated);
    if (handleChange && path) handleChange(path, updated);
  };

  if (!visible) return null;

  if (requiredActivities === "ANY") {
    // Render single generic configuration for ANY mode
    return (
      <div className="activity-streak-config-controller">
        {config.length === 0 && <div>No configuration found.</div>}
        {config.map((item, idx) => (
          <div key={idx} className="activity-config-row">
            <div className="activity-info">
              <span>Campaign</span>
            </div>
            <TextInput
              type="number"
              placeholder="Target Progress"
              min={1}
              value={item.target_points}
              onUpdate={(val) => handleFieldChange(idx, "target_points", val)}
              label="Target Progress"
              required={currentRequiredFields.includes("target_points")}
              id={`target-points-any-${idx}`}
            />
            <TextInput
              type="text"
              placeholder="Label Singular"
              value={item.label_singular}
              onUpdate={(val) => handleFieldChange(idx, "label_singular", val)}
              label="Label Singular"
              required={currentRequiredFields.includes("label_singular")}
              id={`label-singular-any-${idx}`}
            />
            <TextInput
              type="text"
              placeholder="Label Plural"
              value={item.label_plural}
              onUpdate={(val) => handleFieldChange(idx, "label_plural", val)}
              label="Label Plural"
              required={currentRequiredFields.includes("label_plural")}
              id={`label-plural-any-${idx}`}
            />
          </div>
        ))}
      </div>
    );
  }

  // Render activity-specific configurations for ALL mode
  return (
    <div className="activity-streak-config-controller">
      {config.length === 0 && <div>No activities found.</div>}
      {config.map((activity, idx) => (
        <div key={idx} className="activity-config-row">
          <div className="activity-info">
            <span>Activity {idx + 1}:</span> <span>{activity.name}</span>
          </div>
          <TextInput
            type="number"
            placeholder="Target Progress"
            min={1}
            value={activity.target_points}
            onUpdate={(val) => handleFieldChange(idx, "target_points", val)}
            label="Target Progress"
            required={currentRequiredFields.includes("target_points")}
            id={`target-points-all-${idx}`}
          />
          <TextInput
            type="text"
            placeholder="Label Singular"
            value={activity.label_singular}
            onUpdate={(val) => handleFieldChange(idx, "label_singular", val)}
            label="Label Singular"
            required={currentRequiredFields.includes("label_singular")}
            id={`label-singular-all-${idx}`}
          />
          <TextInput
            type="text"
            placeholder="Label Plural"
            value={activity.label_plural}
            onUpdate={(val) => handleFieldChange(idx, "label_plural", val)}
            label="Label Plural"
            required={currentRequiredFields.includes("label_plural")}
            id={`label-plural-all-${idx}`}
          />
        </div>
      ))}
    </div>
  );
};

export { ActivityStreakConfigController };
export const ActivityStreakConfigControllerControl = withJsonFormsControlProps(
  ActivityStreakConfigController
);
